import { flipSign, type Formula, type Role } from "./types"

// Walks a formula tree, assigning each column a role based on its sign in the
// expression. Subtraction flips the sign for everything beneath it.
export function collectRoles(
  formula: Formula,
  sign: 1 | -1 = 1,
  out: Map<string, Role> = new Map(),
  forcedRole?: Role,
): Map<string, Role> {
  const roleFromSign: Role = sign === 1 ? "addend" : "detractor"
  switch (formula.kind) {
    case "col":
      out.set(formula.id, forcedRole ?? roleFromSign)
      break
    case "const":
    case "param":
      break
    case "sum":
      formula.terms.forEach((t) => collectRoles(t, sign, out, forcedRole))
      break
    case "diff":
      collectRoles(formula.from, sign, out, forcedRole)
      formula.minus.forEach((t) => collectRoles(t, flipSign(sign), out, forcedRole))
      break
    case "ratio":
      collectRoles(formula.num, sign, out, forcedRole)
      collectRoles(formula.den, sign, out, "divisor")
      break
    case "product":
      formula.factors.forEach((t) => collectRoles(t, sign, out, forcedRole))
      break
  }
  return out
}

export function collectColumnIds(formula: Formula, out: Set<string> = new Set()): Set<string> {
  switch (formula.kind) {
    case "col":
      out.add(formula.id)
      break
    case "const":
    case "param":
      break
    case "sum":
      formula.terms.forEach((t) => collectColumnIds(t, out))
      break
    case "diff":
      collectColumnIds(formula.from, out)
      formula.minus.forEach((t) => collectColumnIds(t, out))
      break
    case "ratio":
      collectColumnIds(formula.num, out)
      collectColumnIds(formula.den, out)
      break
    case "product":
      formula.factors.forEach((t) => collectColumnIds(t, out))
      break
  }
  return out
}

export type ColResolver = (colId: string) => number

export function evaluateFormula(
  formula: Formula,
  row: Record<string, unknown>,
  params: Record<string, number> = {},
  resolveCol?: ColResolver,
): number {
  switch (formula.kind) {
    case "col":
      return resolveCol ? resolveCol(formula.id) : Number(row[formula.id] ?? 0)
    case "const":
      return formula.value
    case "param":
      return params[formula.id] ?? 0
    case "sum":
      return formula.terms.reduce((s, t) => s + evaluateFormula(t, row, params, resolveCol), 0)
    case "diff":
      return (
        evaluateFormula(formula.from, row, params, resolveCol) -
        formula.minus.reduce((s, t) => s + evaluateFormula(t, row, params, resolveCol), 0)
      )
    case "ratio": {
      const den = evaluateFormula(formula.den, row, params, resolveCol)
      return den === 0 ? 0 : evaluateFormula(formula.num, row, params, resolveCol) / den
    }
    case "product":
      return formula.factors.reduce((p, t) => p * evaluateFormula(t, row, params, resolveCol), 1)
  }
}

// Precedence tiers for parens decisions. Higher = binds tighter. A child whose
// precedence is lower than the parent's "required" tier needs explicit parens
// to disambiguate against the surrounding operator (e.g. `a + b ÷ c` would be
// read by PEMDAS as `a + (b ÷ c)`).
export const PREC = { atom: 3, multiplicative: 2, additive: 1 } as const

export function precedenceOf(node: Formula): number {
  switch (node.kind) {
    case "col":
    case "const":
    case "param":
      return PREC.atom
    case "sum":
    case "diff":
      return PREC.additive
    case "ratio":
    case "product":
      return PREC.multiplicative
  }
}

/**
 * Derives the inspection-mode column state for AG Grid: takes the baseline
 * order and visibility, splices the formula's operand columns immediately
 * after the inspected column, and force-shows every operand + the inspected
 * column itself. Returns `null` when the inspected column isn't in the
 * baseline (e.g. mid-mount race).
 *
 * Pure — no AG Grid imports, no React. Easier to test/reason about than
 * inlining the logic in a useEffect.
 */
export function deriveInspectionColumnState(
  formula: Formula,
  inspectedColId: string,
  baselineOrder: string[],
  baselineHidden: Set<string>,
): { colId: string; hide: boolean }[] | null {
  const related = Array.from(collectColumnIds(formula))
  const withoutRelated = baselineOrder.filter((id) => !related.includes(id))
  const anchorIdx = withoutRelated.indexOf(inspectedColId)
  if (anchorIdx < 0) return null
  const derivedOrder = [
    ...withoutRelated.slice(0, anchorIdx + 1),
    ...related,
    ...withoutRelated.slice(anchorIdx + 1),
  ]
  const forceVisible = new Set<string>([...related, inspectedColId])
  return derivedOrder.map((colId) => ({
    colId,
    hide: forceVisible.has(colId) ? false : baselineHidden.has(colId),
  }))
}
